import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@frontdoor/ui/components/alert';
import { Avatar, AvatarImage } from '@frontdoor/ui/components/avatar';
import { Button } from '@frontdoor/ui/components/button';
import { format } from 'date-fns';
import type { LucideIcon } from 'lucide-react';
import { CircleAlert, Clock, Phone, Video } from 'lucide-react';

import type { TrackingTelehealth, TrackingVerifyPayload } from '@/api/generated';

import { TrackingCard } from './TrackingCard.tsx';

const JOIN_WINDOW_BEFORE_MS = 30 * 60 * 1000;
const DEFAULT_VISIT_DURATION_MINUTES = 60;
const JOIN_WINDOW_POLL_MS = 15_000;

const TIMEZONE_FORMATTER = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });

function getTimezoneAbbr(date: Date): string | undefined {
  return TIMEZONE_FORMATTER.formatToParts(date).find((part) => part.type === 'timeZoneName')?.value;
}

type VisitDetailsCardProps = {
  telehealth: TrackingTelehealth;
  data: TrackingVerifyPayload;
};

function formatScheduledDateTime(scheduledAt: string | null | undefined): string | null {
  if (!scheduledAt) return null;
  const date = new Date(scheduledAt);
  const formatted = format(date, "EEE, MMM do 'at' h:mmaaa");
  const abbr = getTimezoneAbbr(date);
  return abbr ? `${formatted} (${abbr})` : formatted;
}

function VisitHeader({ telehealth }: { telehealth: TrackingTelehealth }) {
  const scheduledDateTime = formatScheduledDateTime(telehealth.scheduled_at);

  return (
    <div className="flex items-center gap-3">
      <Avatar size="lg">
        <AvatarImage src="/provider_avatar.png" alt="" />
      </Avatar>
      <div className="flex flex-col text-sm">
        {scheduledDateTime && <span>{scheduledDateTime}</span>}
        {telehealth.provider_name && (
          <span>
            with <span className="font-medium">{telehealth.provider_name}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function MetaRow({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Icon className="size-5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function isWithinJoinWindow(
  scheduledAt: string | null | undefined,
  durationMinutes: number | null | undefined,
  now: number
): boolean {
  if (!scheduledAt) return false;
  const start = new Date(scheduledAt).getTime();
  if (Number.isNaN(start)) return false;
  const durationMs = (durationMinutes ?? DEFAULT_VISIT_DURATION_MINUTES) * 60 * 1000;
  return now >= start - JOIN_WINDOW_BEFORE_MS && now <= start + durationMs;
}

function useJoinWindow(
  scheduledAt: string | null | undefined,
  durationMinutes: number | null | undefined
): boolean {
  const [isOpen, setIsOpen] = useState(() =>
    isWithinJoinWindow(scheduledAt, durationMinutes, Date.now())
  );

  useEffect(() => {
    function check() {
      setIsOpen(isWithinJoinWindow(scheduledAt, durationMinutes, Date.now()));
    }
    check();
    const id = setInterval(check, JOIN_WINDOW_POLL_MS);
    return () => clearInterval(id);
  }, [scheduledAt, durationMinutes]);

  return isOpen;
}

function VisitKindRow({
  telehealth,
  data,
}: {
  telehealth: TrackingTelehealth;
  data: TrackingVerifyPayload;
}) {
  if (telehealth.kind === 'video') {
    return <MetaRow icon={Video}>Video visit</MetaRow>;
  }

  const { patient, contact } = data.order;
  const calleeName = contact.name ?? patient.first_name;
  const calleePhone = contact.phone_number;

  return (
    <MetaRow icon={Phone}>
      {telehealth.provider_name ?? 'Your provider'} will call {calleeName}
      {calleePhone ? ` at ${calleePhone}` : ''}
    </MetaRow>
  );
}

function JoinVideoVisitButton({ videoUrl }: { videoUrl: string }) {
  return (
    <Button asChild variant="outline" size="sm" className="w-full">
      <a href={videoUrl} target="_blank" rel="noopener noreferrer">
        Join video visit
      </a>
    </Button>
  );
}

const FALLBACK_COPY: Record<'unavailable' | 'not_found', { title: string; description: string }> = {
  unavailable: {
    title: "We're having trouble loading visit details",
    description: 'Please refresh the page in a moment to try again.',
  },
  not_found: {
    title: "We couldn't find this visit in our scheduling system",
    description: 'Please contact us if you think this is a mistake.',
  },
};

function VisitFallback({ status }: { status: 'unavailable' | 'not_found' }) {
  const copy = FALLBACK_COPY[status];
  return (
    <Alert>
      <CircleAlert className="size-4" />
      <AlertTitle>{copy.title}</AlertTitle>
      <AlertDescription>{copy.description}</AlertDescription>
    </Alert>
  );
}

export function VisitDetailsCard({ telehealth, data }: VisitDetailsCardProps) {
  const isJoinable = useJoinWindow(telehealth.scheduled_at, telehealth.duration_minutes);

  if (telehealth.status !== 'ok') {
    return (
      <TrackingCard title="Visit details">
        <VisitFallback status={telehealth.status} />
      </TrackingCard>
    );
  }

  const joinVideoUrl = telehealth.kind === 'video' && isJoinable ? telehealth.video_url : null;

  return (
    <TrackingCard title="Visit details">
      <div className="flex flex-col gap-6">
        <VisitHeader telehealth={telehealth} />

        <hr className="border-t border-border" />

        <div className="flex flex-col gap-2">
          <VisitKindRow telehealth={telehealth} data={data} />
          {telehealth.duration_minutes != null && (
            <MetaRow icon={Clock}>{telehealth.duration_minutes} min</MetaRow>
          )}
        </div>

        {joinVideoUrl && <JoinVideoVisitButton videoUrl={joinVideoUrl} />}
      </div>
    </TrackingCard>
  );
}
